import {
  TransferModel,
  TransferMutationInput,
} from 'api-hooks/transactions/transfer';
import { TransactionalStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, FormState, Input } from 'components/elements';
import { HalfContainer, SeparatorVertical } from 'components/styles';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useVoidAction from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import { SectionContainer, Title, TitleContainer } from 'legacy-modules/styles';
import TransactionPillStatus from 'legacy-modules/transactions/component/status';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { TransferFormType, transferSchema } from './form-type';
import { ReceiverFormCard } from './receiver-form-card';
import { SenderFormCard } from './sender-form-card';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  transfer?: TransferModel;
  onSubmit: (
    input: TransferMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<TransferModel | undefined>;
}

export default function TransferForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { transfer } = props;

  const defaultValues = useGetDefaultValues(transfer);

  const resolver = useYupValidationResolver(transferSchema());

  const methods = useForm<TransferFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        values.needApproval =
          values.transactionAt.getTime() !== values.tempTransactionAt.getTime();

        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e?.message });
      }
    },
    [methods, props],
  );

  const isActive = transfer?.status === TransactionalStatusEnum.Active;
  const permission = useGetCrudPermission('transfers');
  const { onVoid, voidMutate, voidPermission } = useVoidAction(
    'transfers',
    transfer,
  );
  const { requestApprovalButton } = useApprovalAction('transfers', transfer);
  const { resolverButton } = useResolveApproval('transfers', transfer);
  const { revertDraftButton } = useRevertDraftAction('transfers', transfer);

  const onRenderRightComponent = React.useMemo(() => {
    if (transfer) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
        </div>
      );
    }
    return undefined;
  }, [requestApprovalButton, resolverButton, revertDraftButton, transfer]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!transfer}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        transfer && `${t('common:transfer')} - ${transfer.number}`
      }
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={transfer}
            onDelete={onVoid}
            noDelete={editable || !voidPermission}
            noEdit={!isActive || !permission.update}
            title={
              transfer ? (
                <TitleContainer>
                  <Title variant="HeadingXSmall">{`${t('common:transfer')} - ${
                    transfer.number
                  }`}</Title>
                  <TransactionPillStatus value={transfer.status} />
                </TitleContainer>
              ) : (
                t('common:add_extra', { extra: t('common:transfer') })
              )
            }
            isDeleteLoading={voidMutate.isLoading}
            deleteText={t('common:void')}
            onRenderRightComponent={onRenderRightComponent}
          />
        )}
      </FormState>
      <div>
        <SectionContainer>
          {transfer ? (
            <HalfContainer>
              <Input
                type="text"
                name="number"
                label={t('common:number')}
                placeholder={t('common:number')}
                required
                disabled
              />
            </HalfContainer>
          ) : (
            <></>
          )}

          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              name="transactionAt"
              clearable={false}
              hasTime
              required
              label={t('common:transaction_at')}
              placeholder={t('common:transaction_at')}
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="number"
              name="amount"
              label={t('common:amount')}
              placeholder={t('common:amount')}
              required
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="text-area"
              name="description"
              label={t('common:description')}
              placeholder={t('common:description')}
            />
          </HalfContainer>
        </SectionContainer>
        <SeparatorVertical $margin={24} />
        <SectionContainer>
          <SenderFormCard />
          <Separator gap={16} />
          <ReceiverFormCard />
        </SectionContainer>
      </div>
    </Form>
  );
}
